// Client-facing API types. Mirror the server's response shapes
// (packages/server/src/app.ts) and reuse core domain types where they match.
// Implements: architecture.md v1.0 section 5 (API surface).

import type {
  BookingType,
  ConnectionConfig,
  ConnectionId,
  DayRange,
  HostConfig,
  Slot,
  WeeklyHours,
} from '@numaco-meet/core';

export type {
  BookingType,
  ConnectionConfig,
  ConnectionId,
  DayRange,
  HostConfig,
  Slot,
  WeeklyHours,
};

/** A booking as projected to the client by the server (raw token never leaked). */
export interface PublicBooking {
  hostId: string;
  bookingTypeId: string;
  start: string; // ISO-8601 UTC
  end: string; // ISO-8601 UTC
  customer: { name: string; email: string; note?: string };
  status: 'confirmed' | 'cancelled';
  createdAt: string;
  /** Present on the create/reschedule responses. */
  manageLink?: string;
  /**
   * The front buffer, in minutes, captured on the booking RECORD at creation and
   * surfaced on this projection by the server (which sources every display from
   * booking.frontBufferMin; present only when > 0). It lets a client that holds
   * only the booking (the manage page, which has no way to resolve the type on its
   * own, least of all an UNLISTED private type the public booking-types list omits)
   * show the ACTUAL meeting start (nominal start + frontBufferMin), consistent with
   * the calendar invite, the confirmation, and the host email. Because it is read
   * from the record, a historical booking never drifts when the type's buffer is
   * later edited. Display only: start / end stay NOMINAL.
   */
  frontBufferMin?: number;
}

/**
 * The host's PUBLIC profile, as returned by GET /api/hosts/:slug. Strictly
 * public-safe: only the slug and the human-readable name shown on the booking
 * page. Never carries email, connections, tokens, or other private config.
 */
export interface PublicHostResponse {
  slug: string;
  displayName: string;
}

export interface BookingTypesResponse {
  bookingTypes: BookingType[];
}

/**
 * One booking type resolved by its event slug, as returned by
 * GET /api/hosts/:slug/events/:eventSlug (ADR-0003 section 3). The private
 * booking page fetches this once (listed or unlisted) to drive the slot flow for
 * that single event; the `BookingType` carries the new slug/listed/workingHours
 * fields (all optional) inherited from the core domain type.
 */
export interface EventResponse {
  bookingType: BookingType;
}

export interface AvailabilityResponse {
  type: string;
  tz: string;
  slots: Slot[];
}

export interface CreateBookingRequest {
  bookingTypeId: string;
  start: string;
  customer: { name: string; email: string; note?: string };
}

export interface CreateBookingResponse {
  booking: PublicBooking;
  /** Signed token used in the manage link path segment. */
  manageToken: string;
}

export interface BookingResponse {
  booking: PublicBooking;
}

export interface RescheduleResponse {
  booking: PublicBooking;
  manageToken: string;
}

/**
 * Per-source-connection sync health stamps, as surfaced by GET /api/admin/config
 * (calendar-sync.md section 7, health surfacing). A direction's health is its
 * SOURCE connection's record: the sweep stamps success/error at pass end, and
 * `consecutiveFailures` is the current-state signal (reset on success). All
 * fields absent means the direction has never completed a pass.
 */
export interface SyncHealth {
  lastSuccessInstant?: string;
  lastErrorInstant?: string;
  lastErrorMessage?: string;
  consecutiveFailures?: number;
}

export interface AdminConfigResponse {
  config: HostConfig;
  /**
   * Present on GET (admin-authed only, never on a public route): health keyed
   * by each configured sync direction's source connection. Absent on the PUT
   * response, so the page keeps the health it loaded.
   */
  syncHealth?: Partial<Record<ConnectionId, SyncHealth>>;
}

/**
 * A "message Numaco" contact-form submission (POST /api/contact). `website` is a
 * hidden honeypot that a human leaves empty; the server silently drops a filled
 * value. The server validates name (1..100), email (valid, <=200), message
 * (1..4000) and answers 204 on success, 400 invalid, 429 rate-limited, 500 send
 * failure.
 */
export interface ContactRequest {
  name: string;
  email: string;
  message: string;
  /** Honeypot: must stay empty. */
  website?: string;
}

/** The signed-in host as returned by GET /api/me (ADR-0002 section 1). */
export interface SignedInHost {
  hostId: string;
  email: string;
  displayName: string;
  slug: string;
  timezone: string;
}

export interface MeResponse {
  host: SignedInHost;
}

/** Normalized error thrown by the API client for any non-2xx response. */
export class ApiError extends Error {
  readonly status: number;
  /** The server's machine-readable error code, when present. */
  readonly code?: string;
  readonly body?: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    if (code !== undefined) this.code = code;
    this.body = body;
  }
}
